import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { BadgeCheck, ShieldCheck, ArrowRight, Upload, Image as ImageIcon, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function UploadBox({ label, side, sideLabel, value, onChange, testid, hint, replaceText, uploadedText, fileTooBigMsg }) {
  return (
    <label className={`block rounded-2xl border-2 border-dashed p-4 cursor-pointer transition-all ${value ? "border-green-400 bg-green-50" : "border-slate-300 bg-slate-50 hover:border-purple-500 hover:bg-purple-50"}`}>
      <div className="text-xs font-mono-fin uppercase tracking-widest text-slate-500 mb-2">{label}</div>
      {value ? (
        <div className="flex items-start gap-3">
          <img src={value} alt={side} className="w-24 h-16 object-cover rounded-lg border border-slate-200" />
          <div className="text-xs">
            <div className="text-green-700 font-bold">{uploadedText}</div>
            <div className="text-slate-500 mt-0.5">{replaceText}</div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-6 text-slate-500">
          <Upload size={22} />
          <div className="text-sm font-semibold mt-2">{sideLabel}</div>
          <div className="text-[11px] mt-1">{hint}</div>
        </div>
      )}
      <input
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        data-testid={testid}
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          if (f.size > 5 * 1024 * 1024) { toast.error(fileTooBigMsg); return; }
          const url = await fileToDataUrl(f);
          onChange(url);
        }}
      />
    </label>
  );
}

export default function Kyc() {
  const { t } = useTranslation();
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    document_type: "DNI",
    document_number: "",
    date_of_birth: "",
    address: "",
    city: "",
    postal_code: "",
    country: user?.country || "ES",
    phone: "",
    document_front: "",
    document_back: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.document_front || !form.document_back) {
      setError(t("kyc.must_upload"));
      return;
    }
    setLoading(true); setError("");
    try {
      await api.post("/kyc/submit", form);
      await refreshMe();
      toast.success(t("kyc.toast_sent"));
      navigate("/app");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  if (user?.kyc_status === "verified") {
    return (
      <div className="max-w-2xl space-y-5" data-testid="kyc-verified">
        <div className="font-mono-fin text-xs uppercase tracking-widest brand-text font-bold">{t("kyc.eyebrow")}</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter text-slate-900">{t("kyc.verified_title")}</h1>
        <div className="border border-green-300 bg-green-50 rounded-2xl p-5 flex items-start gap-3">
          <BadgeCheck className="text-green-700" size={24} />
          <div>
            <div className="font-semibold text-green-900">{t("kyc.verified_card_title")}</div>
            <div className="text-sm text-green-800">{t("kyc.verified_card_lead")}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6" data-testid="kyc-page">
      <div>
        <div className="font-mono-fin text-xs uppercase tracking-widest brand-text font-bold">{t("kyc.eyebrow_pending")}</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter text-slate-900 mt-1">{t("kyc.title")}</h1>
        <p className="text-slate-500 mt-1">{t("kyc.subtitle")}</p>
      </div>

      {user?.kyc_status === "verification_required" && (
        <div className="rounded-2xl border-2 border-orange-300 bg-orange-50 p-4 flex items-start gap-3" data-testid="kyc-required-banner">
          <AlertTriangle className="text-orange-600 shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-orange-900">
            <b>{t("kyc.required_title")}</b> {t("kyc.required_lead")}
          </div>
        </div>
      )}

      <form onSubmit={onSubmit} className="bg-white border border-slate-200 rounded-3xl p-6 space-y-5" data-testid="kyc-form">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("kyc.doc_type")}</label>
            <select value={form.document_type} onChange={(e) => set("document_type", e.target.value)}
              className="w-full mt-1.5 px-3 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-purple-600 focus:ring-4 focus:ring-purple-100" data-testid="kyc-doctype">
              {["DNI","Pasaporte","NIE","Otro"].map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("kyc.doc_number")}</label>
            <input required value={form.document_number} onChange={(e) => set("document_number", e.target.value)}
              className="w-full mt-1.5 px-4 py-3 rounded-xl border border-slate-200 font-mono-fin outline-none focus:border-purple-600 focus:ring-4 focus:ring-purple-100"
              data-testid="kyc-docnumber" />
          </div>
        </div>

        {/* Document upload — front + back */}
        <div>
          <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500 mb-2 block">
            {t("kyc.doc_uploads_label")} <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <UploadBox label={t("kyc.front")} side="front" sideLabel={t("kyc.upload_front_btn")} value={form.document_front}
                       onChange={(url) => set("document_front", url)} testid="kyc-doc-front"
                       hint={t("kyc.upload_hint")} replaceText={t("kyc.replace")} uploadedText={t("kyc.uploaded")} fileTooBigMsg={t("kyc.file_too_big")} />
            <UploadBox label={t("kyc.back")} side="back" sideLabel={t("kyc.upload_back_btn")} value={form.document_back}
                       onChange={(url) => set("document_back", url)} testid="kyc-doc-back"
                       hint={t("kyc.upload_hint")} replaceText={t("kyc.replace")} uploadedText={t("kyc.uploaded")} fileTooBigMsg={t("kyc.file_too_big")} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("kyc.dob")}</label>
            <input required type="date" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)}
              className="w-full mt-1.5 px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-purple-600 focus:ring-4 focus:ring-purple-100"
              data-testid="kyc-dob" />
          </div>
          <div>
            <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("kyc.phone")}</label>
            <input required value={form.phone} onChange={(e) => set("phone", e.target.value)}
              placeholder="+34 600 000 000"
              className="w-full mt-1.5 px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-purple-600 focus:ring-4 focus:ring-purple-100"
              data-testid="kyc-phone" />
          </div>
        </div>
        <div>
          <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("kyc.address")}</label>
          <input required value={form.address} onChange={(e) => set("address", e.target.value)}
            className="w-full mt-1.5 px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-purple-600 focus:ring-4 focus:ring-purple-100"
            data-testid="kyc-address" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("kyc.city")}</label>
            <input required value={form.city} onChange={(e) => set("city", e.target.value)}
              className="w-full mt-1.5 px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-purple-600 focus:ring-4 focus:ring-purple-100" data-testid="kyc-city" />
          </div>
          <div>
            <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("kyc.postal")}</label>
            <input required value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)}
              className="w-full mt-1.5 px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-purple-600 focus:ring-4 focus:ring-purple-100" data-testid="kyc-cp" />
          </div>
          <div>
            <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("kyc.country")}</label>
            <select value={form.country} onChange={(e) => set("country", e.target.value)}
              className="w-full mt-1.5 px-3 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-purple-600 focus:ring-4 focus:ring-purple-100" data-testid="kyc-country">
              {["ES","DE","FR","IT","PT","NL","BE","IE","AT","FI","LU","GB"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="kyc-error">{error}</div>}

        <div className="flex items-start gap-3 border-l-2 border-purple-600 bg-purple-50 px-3 py-2 text-xs text-slate-700 rounded-r-xl">
          <ShieldCheck size={16} className="text-purple-600 mt-0.5" />
          <span>{t("kyc.privacy")}</span>
        </div>

        <button type="submit" disabled={loading} className="btn-primary" data-testid="kyc-submit">
          {loading ? t("kyc.sending") : <>{t("kyc.submit")} <ArrowRight size={18} /></>}
        </button>
      </form>
    </div>
  );
}
